// Queues replies and new-ticket emails into the outbox, with the toasts and
// cache refreshes that belong to each.
//
// Kept out of the components on purpose: the callbacks below run long after the
// composer that queued them has unmounted, so they must close over nothing but
// the payload and the app-wide singletons (query client, toast, navigator).

import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { postCreateTicket, postReply } from './api';
import { runAfterSend } from './after-send';
import { uploadToAttachments } from './attachment-storage';
import {
  prepareOutbound,
  type ComposeOutboxPayload,
  type ReplyOutboxPayload,
  type UploadProgress,
} from './outbound-send';
import { requestRestore } from './outbox-restore';
import { outboxToastId, SENT_TOAST_MS } from './outbox-toasts';
import { invalidateTicketLists } from './queries';
import { markFiredRemindersDone } from './reminder-actions';
import { outbox, type OutboxJobView } from './send-outbox';
import { humanizeError } from './toast';

// ── Navigation ─────────────────────────────────────────────────────────────
// The "Open ticket" actions run from toasts, possibly with the composer long
// gone. `useSendOutboxToasts` (always mounted, inside the router) registers the
// app's navigate function here. Never `window.location.assign`: a full reload
// would throw away the outbox, which is memory-only.
type Navigate = (to: string) => void;
let appNavigate: Navigate | null = null;
export function setOutboxNavigator(fn: Navigate | null): void {
  appNavigate = fn;
}
const openTicket = (ticketId: string) => appNavigate?.(`/tickets/${ticketId}`);

// ── The job's toast ────────────────────────────────────────────────────────
// One toast follows a job from the Send click to the end, always on
// `outboxToastId(job.id)` (see outbox-toasts.ts for the countdown and spinner
// phases). The success and failure toasts below use the same id, so each one
// replaces the spinner in place rather than stacking beside it.
const dismissJobToast = (jobId: string) => toast.dismiss(outboxToastId(jobId));

/** Retry from anywhere (toast or bubble). The failure toast is NOT dismissed:
 *  the job goes back to `sending`, which turns that same toast into the
 *  spinner again (dismissing first would make sonner drop it). */
export function retryJob(jobId: string): boolean {
  return outbox.retry(jobId);
}

/** Discard from anywhere. Clears the job's toast too. */
export function discardJob(jobId: string): boolean {
  dismissJobToast(jobId);
  return outbox.discard(jobId);
}

/** Take a job back for editing. Clears the job's toast too. */
export function takeJob<P>(jobId: string): OutboxJobView<P> | null {
  dismissJobToast(jobId);
  return outbox.take<P>(jobId);
}

/** Turns the upload pipeline's progress into the spinner's text. */
function uploadStage(report: (stage: string | null) => void) {
  return ({ done, total }: UploadProgress) =>
    report(total <= 1 ? 'Uploading file…' : `Uploading ${done + 1} of ${total} files…`);
}

/** The humanised reason, plus the raw text when it differs so it can still be
 *  reported (same shape as `toastError`). */
function reasonWithDetails(err: unknown): string {
  const human = humanizeError(err);
  const raw = err instanceof Error ? err.message : String(err ?? '');
  if (!raw || raw === human) return human;
  return `${human}\n\nDetails: ${raw.length > 160 ? raw.slice(0, 157) + '…' : raw}`;
}

// ── Replies ────────────────────────────────────────────────────────────────
/** Idempotency keys of replies this browser sent (this session). The composer's
 *  collision warning must not treat your OWN reply, landing a few seconds after you
 *  started the next draft, as "a teammate replied". */
const ownReplyKeys = new Set<string>();
export function getOwnReplyKeys(): ReadonlySet<string> {
  return ownReplyKeys;
}

export function enqueueReply(qc: QueryClient, payload: ReplyOutboxPayload): string {
  const { ticketId } = payload;
  ownReplyKeys.add(payload.idempotency_key);
  const label = payload.ticketSubject ? `Re: ${payload.ticketSubject.slice(0, 80)}` : 'Reply';

  return outbox.enqueue({
    key: ticketId,
    kind: 'reply',
    title: label,
    payload,
    run: async (p, { report }) => {
      const prepared = await prepareOutbound(p.snapshot, {
        upload: uploadToAttachments,
        onProgress: uploadStage(report),
      });
      report(null); // the email itself
      return postReply({
        ticket_id: p.ticketId,
        // Plain-text body — what plain-text mail clients see + the DB column.
        body: prepared.body,
        // Rich HTML — workflow 12 uses it as-is, inline image URLs included.
        body_html: prepared.body_html,
        to: p.to,
        cc: p.cc,
        bcc: p.bcc,
        subject: p.subject,
        idempotency_key: p.idempotency_key,
        attachments: prepared.attachments.length > 0 ? prepared.attachments : undefined,
      });
    },
    onSuccess: async (_result, job) => {
      // Replaces the "Sending…" spinner on the same id. `action` / `cancel`
      // are named so a Retry button from an earlier failure does not survive.
      toast.success('Reply sent', {
        id: outboxToastId(job.id),
        description: payload.ticketSubject ? label : undefined,
        duration: SENT_TOAST_MS,
        dismissible: true,
        action: undefined,
        cancel: undefined,
      });
      // The real message replaces the pending bubble only once this refetch has
      // finished, so the thread never shows a gap or a duplicate. The ticket and
      // the lists too: workflow 12 moved it to waiting_on_client and may have
      // claimed it for the sender.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['ticket-messages', ticketId] }),
        qc.invalidateQueries({ queryKey: ['ticket-events', ticketId] }),
        qc.invalidateQueries({ queryKey: ['ticket', ticketId] }),
      ]);
      invalidateTicketLists(qc);
      // A reply answers a follow-up reminder that has already fired on this ticket.
      markFiredRemindersDone(qc, ticketId);
      // The template's macro ("…then resolve") runs only now, after the refresh above so it acts
      // on the ticket as the reply left it. Fire and forget: the reply is out either way, and a
      // macro failure has its own toast (it must not turn into "your reply didn't send").
      if (payload.afterSend) {
        void runAfterSend(qc, {
          ticketId,
          subject: payload.ticketSubject,
          actorSlackId: payload.actorSlackId ?? null,
          afterSend: payload.afterSend,
        }).catch(() => {});
      }
    },
    onFailure: (err, job) => {
      // Persistent: this is a reply to a client that did not go out, and the
      // person may be on another page by now.
      toast.error(`Your reply didn't send${payload.ticketSubject ? ` — ${label}` : ''}`, {
        id: outboxToastId(job.id),
        // "May not": a timeout can mean Gmail did send. Retrying is still safe
        // (the idempotency key makes the workflow answer `deduped`).
        description: `${reasonWithDetails(err)}\n\nThe client may not have received it. Retry, or open the ticket to check the thread.`,
        duration: Infinity,
        dismissible: true,
        action: {
          label: 'Retry',
          onClick: (e) => {
            // Keep the toast: Retry turns it back into the spinner.
            e.preventDefault();
            void retryJob(job.id);
          },
        },
        cancel: { label: 'Open ticket', onClick: () => openTicket(ticketId) },
      });
    },
  });
}

// ── New ticket (compose) ───────────────────────────────────────────────────
export function enqueueCompose(
  qc: QueryClient,
  payload: ComposeOutboxPayload,
  where: {
    /** Pathname when Send was clicked. The new ticket opens by itself only if
     *  the person is still there — otherwise they get an "Open" button. */
    fromPathname: string;
    /** The pathname right now (injected so this file needs no DOM). */
    currentPathname: () => string;
  },
): string {
  const label = payload.subject.trim() ? `“${payload.subject.trim().slice(0, 60)}”` : 'New ticket';

  return outbox.enqueue({
    key: 'compose',
    kind: 'compose',
    title: label,
    payload,
    run: async (p, { report }) => {
      const prepared = await prepareOutbound(p.snapshot, {
        upload: uploadToAttachments,
        onProgress: uploadStage(report),
      });
      report(null); // the email itself
      const res = await postCreateTicket({
        inbox_id: p.inbox_id,
        to: p.to,
        cc: p.cc,
        bcc: p.bcc,
        subject: p.subject,
        body: prepared.body,
        body_html: prepared.body_html,
        priority: p.priority,
        category: p.category,
        idempotency_key: p.idempotency_key,
        attachments: prepared.attachments.length > 0 ? prepared.attachments : undefined,
      });
      if (!res || !res.ok || !res.ticket_id) {
        throw new Error(res?.error ?? 'The server did not confirm the new ticket');
      }
      return res;
    },
    onSuccess: async (res, job) => {
      const ticketId = res.ticket_id!;
      const stillThere = where.currentPathname() === where.fromPathname;
      // Replaces the "Sending…" spinner on the same id. When the person has
      // moved on it stays longer and offers the new ticket.
      toast.success(
        res.deduped ? 'Already sent — opening the existing ticket' : 'Ticket created and email sent',
        {
          id: outboxToastId(job.id),
          description: label,
          duration: stillThere ? SENT_TOAST_MS : 8000,
          dismissible: true,
          action: stillThere ? undefined : { label: 'Open', onClick: () => openTicket(ticketId) },
          cancel: undefined,
        },
      );
      if (stillThere) openTicket(ticketId);
      invalidateTicketLists(qc);
      await qc.invalidateQueries({ queryKey: ['inbox-new-counts'] });
    },
    onFailure: (err, job) => {
      toast.error(`Your new ticket email didn't send — ${label}`, {
        id: outboxToastId(job.id),
        description: `${reasonWithDetails(err)}\n\nThe customer may not have received it. Retry, or Edit to change it.`,
        duration: Infinity,
        dismissible: true,
        action: {
          label: 'Retry',
          onClick: (e) => {
            e.preventDefault();
            void retryJob(job.id);
          },
        },
        cancel: { label: 'Edit', onClick: () => void requestRestore(job) },
      });
    },
  });
}
